import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    query,
    orderBy
} from "firebase/firestore";
import { db } from "../firebase";
import { INDIAN_STATES } from "../utils/constants";
import Loader from "../components/Loader";

const COLLECTION_NAME = "CUSTOMER_MASTER";

const CustomerMaster = forwardRef((props, ref) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        phone: "",
        gstin: "",
        state: "Tamil Nadu", // Default state
        email: "",
        shippingAddress: "",
        shippingState: "Tamil Nadu",
        sameAsBilling: true
    });
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    useImperativeHandle(ref, () => ({
        openAddModal: () => {
            setEditingId(null);
            setFormData({ name: "", address: "", phone: "", gstin: "", state: "Tamil Nadu", email: "", shippingAddress: "", shippingState: "Tamil Nadu", sameAsBilling: true });
            setShowModal(true);
        }
    }));

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, COLLECTION_NAME), orderBy("name"));
            const snap = await getDocs(q);
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching customers:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone) return alert("Name and Phone are required");

        setLoading(true);
        try {
            if (editingId) {
                await updateDoc(doc(db, COLLECTION_NAME, editingId), formData);
                alert("Customer updated successfully");
            } else {
                await addDoc(collection(db, COLLECTION_NAME), {
                    ...formData,
                    createdAt: Timestamp.now()
                });
                alert("Customer added successfully");
            }
            setShowModal(false);
            setFormData({ name: "", address: "", phone: "", gstin: "", state: "Tamil Nadu", email: "", shippingAddress: "", shippingState: "Tamil Nadu", sameAsBilling: true });
            setEditingId(null);
            fetchCustomers();
        } catch (err) {
            console.error("Error saving customer:", err);
            alert("Failed to save customer");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (customer) => {
        setFormData({
            name: customer.name,
            address: customer.address || "",
            phone: customer.phone || "",
            gstin: customer.gstin || "",
            state: customer.state || "Tamil Nadu",
            email: customer.email || "",
            shippingAddress: customer.shippingAddress || "",
            shippingState: customer.shippingState || "Tamil Nadu",
            sameAsBilling: customer.sameAsBilling ?? (customer.address === customer.shippingAddress && customer.state === customer.shippingState)
        });
        setEditingId(customer.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;
        setLoading(true);
        try {
            await deleteDoc(doc(db, COLLECTION_NAME, id));
            fetchCustomers();
        } catch (err) {
            console.error("Error deleting customer:", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomers = customers.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm) ||
        (c.gstin && c.gstin.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const paginatedCustomers = filteredCustomers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="">
            <div className="card shadow-sm border-0 mb-3">
                <div className="card-body py-2">
                    <div className="row g-2 align-items-center">
                        <div className="col-md-6">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white">🔍</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, phone or GSTIN..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                        <div className="col-md text-end text-muted small">
                            Total {filteredCustomers.length} record(s)
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white py-3">
                        <h5 className="mb-0 text-primary">Customer List</h5>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>GSTIN</th>
                                    <th>State</th>
                                    <th>Address</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCustomers.map(customer => (
                                    <tr key={customer.id} className="table-row-hover">
                                        <td className="fw-bold">{customer.name}</td>
                                        <td>{customer.phone}</td>
                                        <td>{customer.gstin || "-"}</td>
                                        <td>{customer.state}</td>
                                        <td className="small text-muted">{customer.address}</td>
                                        <td className="text-end">
                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(customer)}>✏</button>
                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(customer.id)}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredCustomers.length === 0 && (
                                    <tr><td colSpan="6" className="text-center py-4">No customers found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
                    <div className="small text-muted">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} of {filteredCustomers.length} records
                    </div>
                    <div className="btn-group btn-group-sm shadow-sm">
                        <button
                            className="btn btn-white border px-3"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            &laquo; Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                className={`btn ${currentPage === i + 1 ? 'btn-primary' : 'btn-white border'}`}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            className="btn btn-white border px-3"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            Next &raquo;
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content shadow-lg border-0">
                            <form onSubmit={handleSubmit}>
                                <div className="modal-header bg-light">
                                    <h5 className="modal-title">{editingId ? "Edit Customer" : "Add Customer"}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="fw-bold small mb-1">Customer Name</label>
                                            <input
                                                className="form-control"
                                                required
                                                placeholder="Enter customer name"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="fw-bold small mb-1">Phone Number</label>
                                            <input
                                                className="form-control"
                                                required
                                                placeholder="Enter phone number"
                                                value={formData.phone}
                                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="fw-bold small mb-1">GSTIN (Optional)</label>
                                            <input
                                                className="form-control text-uppercase"
                                                placeholder="22AAAAA0000A1Z5"
                                                value={formData.gstin}
                                                onChange={e => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="fw-bold small mb-1">Email (Optional)</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                placeholder="customer@example.com"
                                                value={formData.email}
                                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="fw-bold small mb-1">Billing State</label>
                                            <select
                                                className="form-select"
                                                value={formData.state}
                                                onChange={e => {
                                                    const newState = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        state: newState,
                                                        shippingState: prev.sameAsBilling ? newState : prev.shippingState
                                                    }));
                                                }}
                                            >
                                                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-12">
                                            <label className="fw-bold small mb-1">Billing Address</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                placeholder="Street address, City, State, ZIP"
                                                value={formData.address}
                                                onChange={e => {
                                                    const newAddr = e.target.value;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        address: newAddr,
                                                        shippingAddress: prev.sameAsBilling ? newAddr : prev.shippingAddress
                                                    }));
                                                }}
                                            ></textarea>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-check form-switch bg-light p-2 ps-5 rounded border">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="sameAsBilling"
                                                    checked={formData.sameAsBilling}
                                                    onChange={e => {
                                                        const checked = e.target.checked;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            sameAsBilling: checked,
                                                            shippingAddress: checked ? prev.address : prev.shippingAddress,
                                                            shippingState: checked ? prev.state : prev.shippingState
                                                        }));
                                                    }}
                                                />
                                                <label className="form-check-label fw-bold small" htmlFor="sameAsBilling">
                                                    Shipping address same as billing
                                                </label>
                                            </div>
                                        </div>
                                        {!formData.sameAsBilling && (
                                            <>
                                                <div className="col-md-6">
                                                    <label className="fw-bold small mb-1">Shipping State</label>
                                                    <select
                                                        className="form-select"
                                                        value={formData.shippingState}
                                                        onChange={e => setFormData({ ...formData, shippingState: e.target.value })}
                                                    >
                                                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-12">
                                                    <label className="fw-bold small mb-1">Shipping Address</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        placeholder="Enter shipping address"
                                                        value={formData.shippingAddress}
                                                        onChange={e => setFormData({ ...formData, shippingAddress: e.target.value })}
                                                    ></textarea>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer bg-light">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : (editingId ? "Update Customer" : "Save Customer")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default CustomerMaster;
